import axios, * as others from 'axios'
import moment from 'moment-timezone'

const state = {
  todaysBookings: undefined,
  currentAdminBooking: undefined,
  currentDate: undefined,
  // The calendar's visible range, in epoch milliseconds, once it has mounted and told us. It is
  // what every refresh loads: a day in the day views, a month in the month view.
  visibleRange: undefined,
}

const mutations = {
  SET_TODAYS_BOOKINGS(state, todaysBookings) {
    state.todaysBookings = todaysBookings
  },
  SET_CURRENT_ADMIN_BOOKING(state, currentAdminBooking) {
    state.currentAdminBooking = currentAdminBooking
  },
  SET_CURRENT_DATE(state, currentDate) {
    state.currentDate = currentDate
  },
  SET_VISIBLE_RANGE(state, visibleRange) {
    state.visibleRange = visibleRange
  },
}

// Admin and external bookings have no job behind them, so the calendar's title and description are
// built from the booking itself.
const withBookingTitle = bookings => {
  for (const booking of bookings) {
    if (!booking.jobNumber) {
      booking.title = booking.calendarId ? 'External - ' : 'Admin - '
      booking.description = booking.bookingTitle
        ? booking.bookingTitle + ' (' + booking.activity + ')'
        : booking.activity
      booking.jobDescription = ''
      booking.status = booking.calendarId ? 'external' : 'admin'
    }
  }
  return bookings
}

const actions = {
  // WHO IS CALLING THIS SO MANY TIMES?

  async setBookingsForDay({ state, commit }, inputs) {
    try {
      const bookings = await axios.get('/api/job/todaysdate/' + inputs.day + '/' + encodeURIComponent(inputs.timezone))
      commit('SET_TODAYS_BOOKINGS', withBookingTitle(bookings.data.bookings))
    } catch (err) {
      console.log('Error getting bookings', err)
    }
  },

  // The calendar reports the range it is showing; loading it here means every later refresh (a
  // save, a delete, the socket) reloads the same range without anyone having to remember it.
  async setVisibleRange({ commit, dispatch }, range) {
    commit('SET_VISIBLE_RANGE', range)
    await dispatch('refreshBookings')
  },

  async setBookingsForRange({ commit }, { start, end }) {
    try {
      const bookings = await axios.get('/api/job/bookingsbetween/' + start + '/' + end)
      commit('SET_TODAYS_BOOKINGS', withBookingTitle(bookings.data.bookings))
    } catch (err) {
      console.log('Error getting bookings', err)
    }
  },

  async refreshBookings({ commit, dispatch, state }, refreshDate) {
    if (refreshDate) {
      commit('SET_CURRENT_DATE', refreshDate)
    }

    // The calendar has told us what it can see, so load that - a day in the day views, a month in
    // the month view - and every refresh (a save, a delete, the socket) stays in step with it.
    if (state.visibleRange) {
      return dispatch('setBookingsForRange', state.visibleRange)
    }

    await dispatch('setBookingsForDay', {
      day: moment.tz(state.currentDate, moment.tz.guess()).format('DD-MM-YYYY'),
      timezone: moment.tz.guess(),
    })
  },

  async moveBooking({ state, rootGetters, dispatch }, movingEvent) {
    const eventBooking = movingEvent.event.extendedProps.originalBookingEvent

    const booking = state.todaysBookings.find(bookingEvent => {
      return bookingEvent.sequence === eventBooking.sequence
    })

    let bookingToSave = undefined
    let currentJob = undefined
    if (booking.bookingEvents[0].isAdminType) {
      if (eventBooking.status === 'external') {
        await dispatch('refreshBookings')
        throw new Error('An external booking cannot be modified')
      }

      const bookingRequest = await axios.get('/api/booking/sequence/' + booking.sequence)
      bookingToSave = bookingRequest.data.bookings[0]
    } else {
      if (eventBooking.status === 'unfuckable' && !rootGetters['auth/hasRole']('admin')) {
        await dispatch('refreshBookings')
        throw new Error('You must be an admin to modify an invoiced Job')
      }

      if (eventBooking.status === 'completed' && !rootGetters['auth/hasRole']('admin')) {
        await dispatch('refreshBookings')
        throw new Error('A completed booking cannot be modified')
      }
      await dispatch('job/setJobByBooking', { sequence: booking.sequence }, { root: true })
      currentJob = rootGetters['job/currentJob']
      bookingToSave = currentJob.bookings.find(b => b.sequence === booking.sequence)
    }

    if (!bookingToSave) {
      await dispatch('refreshBookings')
      throw new Error('This booking is corrupt and cannot be moved, contact support')
    }

    bookingToSave.startsAt = movingEvent.event.start
    bookingToSave.endsAt = movingEvent.event.end
    bookingToSave.hours = moment
      .duration(
        moment(bookingToSave.endsAt, 'DD-MM-YYYY HH:mm').diff(moment(bookingToSave.startsAt, 'DD-MM-YYYY HH:mm'))
      )
      .asHours()

    //change resources if we need to
    if (movingEvent.newResource) {
      for (const bookingEvent of bookingToSave.bookingEvents) {
        if (bookingEvent.resource._id === movingEvent.oldResource.id) {
          bookingEvent.resource = movingEvent.newResource.id
        }
      }
    }

    await dispatch('saveBooking', bookingToSave)
  },

  saveBooking: async ({ dispatch, rootGetters }, booking) => {
    const currentJob = rootGetters['job/currentJob']

    if (currentJob) {
      await dispatch('job/setJob', currentJob, { root: true })
    } else {
      if (booking.jobNumber) {
        await dispatch('job/setJobByBooking', { sequence: booking.sequence }, { root: true })
      }
    }

    booking.startsAt = moment(booking.startsAt, 'DD-MM-YYYY HH:mm').toDate()
    booking.endsAt = moment(booking.endsAt, 'DD-MM-YYYY HH:mm').toDate()
    if (booking.endsOn) {
      booking.endsOn = moment(booking.endsOn, 'DD-MM-YYYY HH:mm').toDate()
    }
    booking.timezone = moment.tz.guess()

    await dispatch('job/save', booking, { root: true })

    await dispatch('refreshBookings')
  },

  copyBooking: ({}, bookingCopiedEvent) => {
    localStorage.copiedBookingEvent = JSON.stringify({
      booking: bookingCopiedEvent.data.extendedProps.originalBookingEvent,
      timestamp: Math.floor(Date.now() / 1000),
    })
  },

  pasteBooking: async ({ dispatch, rootGetters, state }, pasteData) => {
    if (pasteData.date) {
      // `readCopiedBooking`, not a bare JSON.parse: Paste is reachable whether or not anything was
      // copied, and JSON.parse(undefined) threw a SyntaxError into the console.
      const copiedBookingEvent = readCopiedBooking()

      if (!copiedBookingEvent) {
        return false
      }

      if (copiedBookingEvent.bookingEvents[0].isAdminType) {
        const copiedBooking = await dispatch('findAdminBookingByBookingSequence', copiedBookingEvent.sequence)

        const bookingSequence = await dispatch('sequence/next', 'adminBooking', { root: true })
        var booking = {
          startsAt: pasteData.date.toDate(),
          endsAt: pasteData.date.add(copiedBooking.hours, 'hours').toDate(),
          sequence: bookingSequence,
          status: copiedBooking.status,
          activity: copiedBooking.activity,
          hours: copiedBooking.hours,
          notes: copiedBooking.notes,
          createdAt: copiedBooking.createdAt,
          createdBy: copiedBooking.createdBy,
        }
        await addBookingEvents(dispatch, rootGetters, booking, copiedBooking, pasteData)
      } else {
        await dispatch('job/setJobByBooking', { sequence: copiedBookingEvent.sequence }, { root: true })
        const currentJob = rootGetters['job/currentJob']
        const copiedBooking = currentJob.bookings.filter(booking => {
          return booking.sequence === copiedBookingEvent.sequence
        })[0]

        const bookingSequence = await dispatch('sequence/next', 'booking', { root: true })
        var booking = {
          startsAt: pasteData.date.toDate(),
          endsAt: pasteData.date.add(copiedBooking.hours, 'hours').toDate(),
          sequence: bookingSequence,
          jobNumber: copiedBooking.jobNumber,
          status: copiedBooking.status,
          activity: copiedBooking.activity,
          hours: copiedBooking.hours,
          notes: copiedBooking.notes,
          createdAt: copiedBooking.createdAt,
          createdBy: copiedBooking.createdBy,
        }
        await addBookingEvents(dispatch, rootGetters, booking, copiedBooking, pasteData)
      }

      return true
    }

    return false
  },

  findAdminBookingByBookingSequence: async ({ commit }, bookingEventSequence) => {
    const httpResult = await axios.get('/api/booking/sequence/' + bookingEventSequence)
    return httpResult.data.bookings[0]
  },
}

/**
 * The booking `copyBooking` left on the clipboard, or null when there is not one.
 *
 * Paste is reachable without a copy, so this must not throw: `JSON.parse(undefined)` is a
 * SyntaxError, and localStorage can hold something that is not JSON at all.
 */
const readCopiedBooking = () => {
  try {
    const stored = localStorage.copiedBookingEvent
    return stored ? JSON.parse(stored).booking : null
  } catch (err) {
    return null
  }
}

const addBookingEvents = async (dispatch, rootGetters, booking, copiedBooking, pasteData) => {
  booking.bookingEvents = []

  // find the current event sequences
  var bookingEventSequencePromises = []
  for (const bookingEvent of copiedBooking.bookingEvents) {
    bookingEventSequencePromises.push(dispatch('sequence/next', 'bookingEvent', { root: true }))
  }

  const sequences = await Promise.all(bookingEventSequencePromises)
  const resource = rootGetters['resource/findResourceById'](pasteData.resourceId)

  // push bookingevents on the new booking for each bookingevent (with current resources if > 1)
  if (sequences.length > 1) {
    for (var i = 0; i < sequences.length; i++) {
      booking.bookingEvents.push({
        sequence: sequences[i],
        resource: copiedBooking.bookingEvents[i].resource,
        parentBookingSequence: booking.sequence,
        isAdminType: copiedBooking.bookingEvents[i].isAdminType,
      })
    }
  } else {
    booking.bookingEvents.push({
      sequence: sequences[0],
      resource: resource,
      parentBookingSequence: booking.sequence,
      isAdminType: copiedBooking.bookingEvents[0].isAdminType,
    })
  }

  const currentJob = rootGetters['job/currentJob']
  if (rootGetters['job/currentJob']) {
    currentJob.bookings.push(booking)
    await dispatch('job/save', booking, { root: true })
    await dispatch('refreshBookings')
  } else {
    await dispatch('saveBooking', booking)
  }
}

const getters = {
  currentAdminBooking: state => {
    return state.currentAdminBooking
  },
}

export default {
  namespaced: true,
  state: state,
  mutations: mutations,
  actions: actions,
  getters: getters,
}
